import React from 'react';
import clsx from 'clsx';
import BiomarkerDisplayRulesPanel from '@/modules/admin/components/BiomarkerDisplayRulesPanel';
import AdminLoincCodesPanel from '@/modules/admin/components/AdminLoincCodesPanel';
import UnmappedAnalytesPanel from '@/modules/admin/components/UnmappedAnalytesPanel';
import type {UnmappedAnalyteSuggestion} from '@/shared/types/api';

export type BiomarkersTab = 'rules' | 'needs-mapping' | 'reference';

/** What the other tabs hand to the rules tab: the code, plus a pre-filled
 *  draft when it came from Needs mapping. */
interface CurateRequest {
    loinc: string;
    suggestion?: UnmappedAnalyteSuggestion;
    note?: string;
}

/**
 * The single "Biomarkers" admin page.
 *
 * There used to be two menu entries — "LOINC Codes" and "Biomarker Display
 * Rules" — and both let an admin type a patient-facing name. Only one of them
 * (the rules) ever reached a patient, so editing the other was a silent no-op
 * with a success toast. One page: the Display rules tab is the one editor;
 * Needs mapping and the LOINC reference tab are read-only lists whose per-row
 * links land back on the rules tab.
 */
const BiomarkersAdminPage: React.FC<{initialTab?: BiomarkersTab}> = ({initialTab = 'rules'}) => {
    const [tab, setTab] = React.useState<BiomarkersTab>(initialTab);
    // The code handed over by "Curate" / "Map it"; the rules panel consumes
    // it (jump-to-rule or pre-filled Add rule) and clears it.
    const [curate, setCurate] = React.useState<CurateRequest | null>(null);
    const consumeCurate = React.useCallback(() => setCurate(null), []);
    const sendToRules = (request: CurateRequest) => {
        setCurate(request);
        setTab('rules');
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-1 border-b border-mld-panel-border">
                {(
                    [
                        {key: 'rules', label: 'Display rules'},
                        {key: 'needs-mapping', label: 'Needs mapping'},
                        {key: 'reference', label: 'LOINC reference'},
                    ] as const
                ).map(({key, label}) => (
                    <button
                        key={key}
                        type="button"
                        onClick={() => setTab(key)}
                        className={clsx(
                            '-mb-px rounded-t-md border-b-2 px-4 py-2 text-sm font-semibold transition',
                            tab === key
                                ? 'border-mld-primary text-mld-primary'
                                : 'border-transparent text-mld-muted-text hover:text-mld-menu-text'
                        )}>
                        {label}
                    </button>
                ))}
            </div>

            {tab === 'rules' ? (
                <BiomarkerDisplayRulesPanel
                    curateLoinc={curate?.loinc ?? null}
                    curateSuggestion={curate?.suggestion ?? null}
                    curateNote={curate?.note ?? null}
                    onCurateConsumed={consumeCurate}
                />
            ) : null}
            {tab === 'needs-mapping' ? (
                <UnmappedAnalytesPanel
                    onMap={(row) => {
                        if (!row.suggestion) return;
                        sendToRules({
                            loinc: row.suggestion.loinc,
                            suggestion: row.suggestion,
                            note: `Mapped from Needs mapping: ${row.source.toUpperCase()} ${row.rawIdentifier}`,
                        });
                    }}
                />
            ) : null}
            {tab === 'reference' ? <AdminLoincCodesPanel onCurate={(loinc) => sendToRules({loinc})} /> : null}
        </div>
    );
};

export default BiomarkersAdminPage;
